import * as Y from "yjs";

const DEFAULT_ORIGIN = "spry-crdt";

const cloneValue = <T>(value: T): T => {
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }
  return value == null ? value : JSON.parse(JSON.stringify(value)) as T;
};

const serialize = (value: unknown): string => JSON.stringify(value ?? null);

const deserialize = <T>(value: unknown, fallback: T): T => {
  if (typeof value !== "string" || value.length === 0) {
    return fallback;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
};

export interface SpryCollaborationCrdtChangeEvent<TSnapshot = unknown, TPresence = unknown> {
  origin: string | null;
  update: Uint8Array;
  snapshot: TSnapshot | null;
  presence: Record<string, TPresence | null>;
  operationsCount: number;
  stateVector: Uint8Array;
}

export interface SpryCollaborationCrdtOptions<TSnapshot = unknown> {
  name?: string;
  initialSnapshot?: TSnapshot | null;
  snapshotKey?: string;
  operationsKey?: string;
  presenceKey?: string;
  metaKey?: string;
}

type SpryCollaborationOperation = {
  id: string;
  kind: string;
  payload: unknown;
  origin: string;
  timestamp: number;
};

export class SpryCollaborationCrdt<TSnapshot = unknown, TPresence = unknown> {
  readonly name: string;
  private readonly doc: Y.Doc;
  private readonly snapshotMap: Y.Map<string>;
  private readonly operations: Y.Array<string>;
  private readonly presenceMap: Y.Map<string>;
  private readonly metaMap: Y.Map<unknown>;
  private readonly listeners = new Set<(event: SpryCollaborationCrdtChangeEvent<TSnapshot, TPresence>) => void>();
  private readonly boundUpdateHandler: (update: Uint8Array, origin: unknown) => void;
  private lastOrigin: string | null = null;

  constructor(options: SpryCollaborationCrdtOptions<TSnapshot> = {}) {
    const {
      name = "collaboration",
      initialSnapshot = null,
      snapshotKey = "snapshot",
      operationsKey = "operations",
      presenceKey = "presence",
      metaKey = "meta",
    } = options;

    this.name = name;
    this.doc = new Y.Doc();
    this.snapshotMap = this.doc.getMap<string>(snapshotKey);
    this.operations = this.doc.getArray<string>(operationsKey);
    this.presenceMap = this.doc.getMap<string>(presenceKey);
    this.metaMap = this.doc.getMap<unknown>(metaKey);
    this.boundUpdateHandler = (update, origin) => {
      this.lastOrigin = typeof origin === "string" ? origin : origin == null ? null : String(origin);
      if (this.listeners.size === 0) {
        return;
      }

      const changeEvent: SpryCollaborationCrdtChangeEvent<TSnapshot, TPresence> = {
        origin: this.lastOrigin,
        update,
        snapshot: this.getSnapshot(),
        presence: this.getPresenceStates(),
        operationsCount: this.getOperationCount(),
        stateVector: this.encodeStateVector(),
      };
      for (const listener of this.listeners) {
        listener(changeEvent);
      }
    };

    this.doc.on("update", this.boundUpdateHandler);

    if (initialSnapshot !== null) {
      this.replaceSnapshot(initialSnapshot, `${this.name}:bootstrap`);
    }
  }

  transact(mutator: () => void, origin = DEFAULT_ORIGIN): void {
    this.doc.transact(mutator, origin);
  }

  replaceSnapshot(snapshot: TSnapshot | null, origin = `${this.name}:snapshot:replace`): void {
    this.doc.transact(() => {
      this.snapshotMap.set("json", serialize(snapshot));
      this.metaMap.set("updatedAt", Date.now());
    }, origin);
  }

  mergeSnapshot(patch: Partial<TSnapshot>, origin = `${this.name}:snapshot:merge`): void {
    const currentSnapshot = this.getSnapshot() ?? ({} as TSnapshot);
    this.replaceSnapshot(
      {
        ...(currentSnapshot as object),
        ...(cloneValue(patch) as object),
      } as TSnapshot,
      origin,
    );
  }

  mutateSnapshot(mutator: (snapshot: TSnapshot | Record<string, unknown>) => void | TSnapshot, origin = `${this.name}:snapshot:mutate`): void {
    const nextSnapshot = cloneValue(this.getSnapshot() ?? ({} as TSnapshot | Record<string, unknown>));
    const result = mutator(nextSnapshot);
    this.replaceSnapshot((result === undefined ? nextSnapshot : result) as TSnapshot, origin);
  }

  clearSnapshot(origin = `${this.name}:snapshot:clear`): void {
    this.doc.transact(() => {
      this.snapshotMap.delete("json");
      this.metaMap.set("updatedAt", Date.now());
    }, origin);
  }

  getSnapshot(): TSnapshot | null {
    return deserialize<TSnapshot | null>(this.snapshotMap.get("json"), null);
  }

  setMetadata(key: string, value: unknown, origin = `${this.name}:meta:set`): void {
    this.doc.transact(() => {
      this.metaMap.set(key, cloneValue(value));
    }, origin);
  }

  getMetadata(key: string): unknown {
    return this.metaMap.get(key);
  }

  getAllMetadata(): Record<string, unknown> {
    return Object.fromEntries(this.metaMap.entries());
  }

  appendOperation(kind: string, payload: unknown, origin = `${this.name}:operation:append`): {
    id: string;
    kind: string;
    payload: unknown;
    origin: string;
    timestamp: number;
  } {
    const operation = {
      id: `${this.name}:${Date.now()}:${this.operations.length + 1}`,
      kind,
      payload: cloneValue(payload),
      origin,
      timestamp: Date.now(),
    };
    this.doc.transact(() => {
      this.operations.push([serialize(operation)]);
      this.metaMap.set("updatedAt", Date.now());
    }, origin);
    return operation;
  }

  getOperations(): SpryCollaborationOperation[] {
    return this.operations.toArray().reduce<SpryCollaborationOperation[]>((result, entry) => {
      const operation = deserialize<SpryCollaborationOperation | null>(entry, null);
      if (operation) {
        result.push(operation);
      }
      return result;
    }, []);
  }

  getOperationCount(): number {
    return this.operations.length;
  }

  clearOperations(origin = `${this.name}:operation:clear`): void {
    this.doc.transact(() => {
      if (this.operations.length > 0) {
        this.operations.delete(0, this.operations.length);
      }
      this.metaMap.set("updatedAt", Date.now());
    }, origin);
  }

  setPresenceState(id: string, state: TPresence, origin = `${this.name}:presence:set`): void {
    if (!id) return;
    this.doc.transact(() => {
      this.presenceMap.set(id, serialize(state));
      this.metaMap.set("updatedAt", Date.now());
    }, origin);
  }

  removePresenceState(id: string, origin = `${this.name}:presence:remove`): void {
    if (!id) return;
    this.doc.transact(() => {
      this.presenceMap.delete(id);
      this.metaMap.set("updatedAt", Date.now());
    }, origin);
  }

  replacePresenceStates(states: Record<string, TPresence>, origin = `${this.name}:presence:replace`): void {
    const entries = Object.entries(states ?? {});
    this.doc.transact(() => {
      Array.from(this.presenceMap.keys()).forEach((key) => this.presenceMap.delete(key));
      entries.forEach(([key, value]) => {
        this.presenceMap.set(key, serialize(value));
      });
      this.metaMap.set("updatedAt", Date.now());
    }, origin);
  }

  clearPresenceStates(origin = `${this.name}:presence:clear`): void {
    this.doc.transact(() => {
      Array.from(this.presenceMap.keys()).forEach((key) => this.presenceMap.delete(key));
      this.metaMap.set("updatedAt", Date.now());
    }, origin);
  }

  getPresenceStates(): Record<string, TPresence | null> {
    const next: Record<string, TPresence | null> = {};
    for (const [key, value] of this.presenceMap.entries()) {
      next[key] = deserialize<TPresence | null>(value, null);
    }
    return next;
  }

  observe(listener: (event: SpryCollaborationCrdtChangeEvent<TSnapshot, TPresence>) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  encodeUpdate(stateVector?: Uint8Array): Uint8Array {
    if (stateVector) {
      return Y.encodeStateAsUpdate(this.doc, stateVector);
    }
    return Y.encodeStateAsUpdate(this.doc);
  }

  encodeStateVector(): Uint8Array {
    return Y.encodeStateVector(this.doc);
  }

  applyUpdate(update: Uint8Array, origin = `${this.name}:update:apply`): void {
    Y.applyUpdate(this.doc, update, origin);
  }

  destroy(): void {
    this.listeners.clear();
    this.doc.off("update", this.boundUpdateHandler);
    this.doc.destroy();
  }
}
